import { useDrag } from '../../../hooks/useDrag';
import { RefObject, useRef, useEffect, useCallback } from 'react';
import { ReactMouseEvent, ReactKeyboardEvent } from '../../../react-synthetic-events';
import { useIsRTL } from '../../../locale';

export interface DraggableProps {
  /**
   * Ref to draggable element.
   */
  ref: RefObject<HTMLElement>;
  bounds?: { left: number; right: number; top: number; bottom: number };
  initialPosition: { x: number; y: number };
  dragHandle?: string;
  dragCancelHandle?: string[];
  onDragStart?: () => void;
  onDrag?: (position: { x: number; y: number }) => void;
  onDragEnd?: (position: { x: number; y: number }) => void;
}

export function useDraggable(props: DraggableProps) {
  const ref = props.ref;
  const dragHandle = props.dragHandle ?? '';
  const dragCancelHandle = props.dragCancelHandle ?? '';
  const initialPosition = props.initialPosition ?? { x: 0, y: 0 };
  const bounds = useRef({ left: NaN, right: NaN, top: NaN, bottom: NaN });
  const isRTL = useIsRTL();

  const calculateBounds = useCallback(() => {
    if (!props.bounds) {
      const node = ref.current;
      const offsetParent = node?.offsetParent;
      if (!node || !offsetParent) return;
      const boundRect = node.getBoundingClientRect();

      const viewportWidth = offsetParent.clientWidth - 64;
      const viewportHeight = offsetParent.clientHeight - 64;

      bounds.current = {
        left: isRTL ? -(viewportWidth / 2 - boundRect.width) : -(viewportWidth / 2),
        top: -(viewportHeight / 2),
        right: isRTL ? viewportWidth / 2 : viewportWidth / 2 - boundRect.width,
        bottom: viewportHeight / 2 - boundRect.height,
      };
    } else {
      bounds.current = props.bounds;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.bounds, props.initialPosition, ref, isRTL]);

  useEffect(() => {
    calculateBounds();
    window.addEventListener('resize', calculateBounds);
    return () => {
      window.removeEventListener('resize', calculateBounds);
    };
  }, [calculateBounds]);

  const validateDragHandle = (targetNode: HTMLElement) => {
    if (dragHandle === '') return true;

    const baseNode = ref.current as HTMLElement;
    do {
      if (targetNode.matches(`.${dragHandle}`)) return true;
      if (targetNode === baseNode) return false;
      targetNode = targetNode.parentNode as HTMLElement;
    } while (targetNode);
  };

  const validateDragCancelHandle = (targetNode: HTMLElement) => {
    if (dragCancelHandle === '') return false;

    const baseNode = ref.current as HTMLElement;
    const checkCondition = (node: HTMLElement) => {
      for (const value of dragCancelHandle) {
        if (node.matches && node.matches(`.${value}`)) return true;
      }
      return false;
    };
    do {
      if (checkCondition(targetNode)) return true;
      if (targetNode === baseNode) return false;
      targetNode = targetNode.parentNode as HTMLElement;
    } while (targetNode);
  };

  const checkBounds = (x: number, y: number) => {
    x = Math.min(x, bounds.current.right);
    y = Math.min(y, bounds.current.bottom);

    x = Math.max(x, bounds.current.left);
    y = Math.max(y, bounds.current.top);

    return { x, y };
  };

  const { startDrag } = useDrag({
    onDrag: (_e, position) => {
      const newPosition = {
        x: initialPosition.x + position.deltaX,
        y: initialPosition.y + position.deltaY,
      };
      props.onDrag?.(checkBounds(newPosition.x, newPosition.y));
    },
    onDragEnd: (_e, position) => {
      const newPosition = {
        x: initialPosition.x + position.deltaX,
        y: initialPosition.y + position.deltaY,
      };
      props.onDragEnd?.(checkBounds(newPosition.x, newPosition.y));
    },
  });

  const onArrowKeyDrag = (e: ReactKeyboardEvent) => {
    let newPosition;
    switch (e.key) {
      case 'ArrowLeft':
        newPosition = {
          x: initialPosition.x - 5,
          y: initialPosition.y,
        };
        props.onDragEnd?.(checkBounds(newPosition.x, newPosition.y));
        break;
      case 'ArrowRight':
        newPosition = {
          x: initialPosition.x + 5,
          y: initialPosition.y,
        };
        props.onDragEnd?.(checkBounds(newPosition.x, newPosition.y));
        break;
      case 'ArrowUp':
        newPosition = {
          x: initialPosition.x,
          y: initialPosition.y - 5,
        };
        props.onDragEnd?.(checkBounds(newPosition.x, newPosition.y));
        break;
      case 'ArrowDown':
        // prevent dragging post hitting bottom of viewport
        if (ref.current) {
          const nodeBoundingRect = ref.current.getBoundingClientRect();
          const parent = ref.current.offsetParent;
          if (parent) {
            if (
              Math.round(nodeBoundingRect.y + 5 + nodeBoundingRect.height) >=
              Math.round(parent.getBoundingClientRect().height - 32)
            ) {
              return;
            }
          }
        }
        newPosition = {
          x: initialPosition.x,
          y: initialPosition.y + 5,
        };
        props.onDragEnd?.(checkBounds(newPosition.x, newPosition.y));
        break;
    }
  };
  return {
    startDragging: (e: ReactMouseEvent) => {
      if (
        e.button !== 0 ||
        !validateDragHandle(e.target as HTMLElement) ||
        validateDragCancelHandle(e.target as HTMLElement)
      )
        return;
      startDrag([e.clientX, e.clientY]);
      props.onDragStart?.();
    },
    onArrowKeyDrag,
    calculateBounds,
  };
}
